import React, { useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import AadhaarImageBox from "./AadhaarImageBox";
import useAadhaarImageUpload from "../../hooks/useAadhaarImageUpload";
import { normalizeDobForInput, areDetailsValid } from "../../utils/aadhaarImageHelpers";

const STEP = { FIRST: "first", SECOND: "second" };

const SIDE_TEXT = {
  front: {
    title: "Now upload the FRONT side of the Aadhaar card",
    hint: "Front side of Aadhaar card",
    wrong: "Back side was uploaded again. Please upload the FRONT side of the Aadhaar card.",
  },
  back: {
    title: "Now upload the BACK side of the Aadhaar card",
    hint: "Back side of Aadhaar card",
    wrong: "Front side was uploaded again. Please upload the BACK side of the Aadhaar card.",
  },
};

/**
 * Aadhaar image upload flow (same checks as the worker signup flow).
 * Calls onComplete({ dob, address, name, aadhaarNo, complete }):
 *   complete = true  → dob + address read and valid
 *   complete = false → something missing/invalid, parent should show manual entry
 *
 * isEdit = true only when an already-verified Aadhaar is being edited via image.
 * It is sent to upload-aadhaar-image on every upload in that case (front, back,
 * or re-upload), and never sent otherwise.
 */
const AadhaarImageUploadStep = ({ workerId, onComplete, onBack, isEdit = false }) => {
  const { uploadSide, isUploading } = useAadhaarImageUpload(workerId);

  const [step, setStep] = useState(STEP.FIRST);
  const [firstFile, setFirstFile] = useState(null);
  const [secondFile, setSecondFile] = useState(null);
  const [needSide, setNeedSide] = useState(null);
  const [sideError, setSideError] = useState("");
  const [partial, setPartial] = useState({ dob: "", address: "", name: "", aadhaarNo: "" });

  const canContinue =
    (step === STEP.FIRST && !!firstFile) || (step === STEP.SECOND && !!secondFile);

  const finish = ({ dob, address, name, aadhaarNo }) => {
    const finalDob = normalizeDobForInput(dob);
    onComplete({
      dob: finalDob,
      address: address || "",
      name: name || "",
      aadhaarNo: aadhaarNo || "",
      complete: areDetailsValid(finalDob, address),
    });
  };

  const handleContinue = async () => {
    try {
      if (step === STEP.FIRST) {
        const data = await uploadSide(firstFile, "front", isEdit);

        // nothing readable → stay here, ask for a clearer photo
        if (!data.dob && !data.address) {
          setSideError("Could not read the Aadhaar. Please upload a clearer photo.");
          setFirstFile(null);
          return;
        }
        // single image had both
        if (data.dob && data.address) return finish(data);

        // only one side read → ask for the other
        setPartial({
          dob: data.dob || "",
          address: data.address || "",
          name: data.name || "",
          aadhaarNo: data.aadhaarNo || "",
        });
        setNeedSide(data.dob ? "back" : "front");
        setSideError("");
        setStep(STEP.SECOND);
        return;
      }

      const data = await uploadSide(secondFile, needSide, isEdit);
      const gotNeeded = needSide === "back" ? !!data.address : !!data.dob;

      // same side uploaded again → don't proceed
      if (!gotNeeded) {
        setSideError(SIDE_TEXT[needSide].wrong);
        setSecondFile(null);
        return;
      }
      finish({
        dob: partial.dob || data.dob,
        address: partial.address || data.address,
        name: partial.name || data.name,
        aadhaarNo: partial.aadhaarNo || data.aadhaarNo,
      });
    } catch (err) {
      console.error("Aadhaar image step failed:", err);
      toast.error(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="space-y-4">
      <AadhaarImageBox
        title="Upload the Aadhaar card image"
        hint="Any side of Aadhaar card"
        file={firstFile}
        onChange={(f) => { setSideError(""); setFirstFile(f); }}
        disabled={step !== STEP.FIRST || isUploading}
        externalError={step === STEP.FIRST ? sideError : ""}
      />

      {step === STEP.SECOND && (
        <AadhaarImageBox
          title={SIDE_TEXT[needSide].title}
          hint={SIDE_TEXT[needSide].hint}
          file={secondFile}
          onChange={(f) => { setSideError(""); setSecondFile(f); }}
          disabled={isUploading}
          externalError={sideError}
        />
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={isUploading}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition disabled:opacity-50"
        >
          <RefreshCw size={14} /> Back
        </button>
        <button
          type="button"
          onClick={handleContinue}
          disabled={!canContinue || isUploading}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {isUploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Reading Aadhaar...</> : "Continue"}
        </button>
      </div>
    </div>
  );
};

export default AadhaarImageUploadStep;